import { Download, FilePenLine, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";

import { type TaxForm } from "@/api-v3";
import { CopyableId } from "@/components/copyable-id";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatISODate } from "@/lib/date-formatter";
import { openLink } from "@/lib/routing";
import { cn, formatKeyText } from "@/lib/utils";

import { getStatusStyles } from "../../../helpers/get-status-styles";

interface TaxFormItemProps {
  item: TaxForm;
}

function actionFor(item: TaxForm, t: (key: string) => string) {
  const status = item.form_status.toLowerCase();
  const isCompleted =
    status === "completed" || status === "completed_and_tin_match_inprogress";

  if (isCompleted && item.download_url) {
    const downloadUrl = item.download_url;
    return (
      <Button
        size="sm"
        variant="outline"
        onClick={() => window.open(downloadUrl, "_blank")}
      >
        <Download />
        {t("button_download_pdf")}
      </Button>
    );
  }

  if (!isCompleted && item.form_url) {
    const formUrl = item.form_url;
    return (
      <Button
        size="sm"
        variant="outline"
        onClick={() => openLink(formUrl, "_self")}
      >
        <FilePenLine />
        {t("tax_form_continue")}
      </Button>
    );
  }
  return null;
}

export const TaxFormItem = ({ item }: TaxFormItemProps) => {
  const { t } = useTranslation();

  const parsedFormStatus = item.form_status.toLowerCase();
  const formStatus =
    parsedFormStatus === "url_generated" ? "pending" : parsedFormStatus;
  const action = actionFor(item, t);

  return (
    <div
      className={cn(
        "relative w-full bg-white rounded-2xl border border-border p-5 shadow-xs",
        "hover:shadow-md hover:border-primary/40 transition-all"
      )}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="shrink-0">
            <div className="flex items-center justify-center h-10 w-10 rounded-full bg-slate-100 text-slate-500 user-select-none">
              <FileText />
            </div>
          </div>
          <div className="min-w-0 pt-1">
            <h3 className="text-lg font-semibold leading-tight truncate">
              {item.form_type ?? t("tax_form")}
            </h3>
            {item.updated_at && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {t("lastUpdate", {
                  date: formatISODate(item.updated_at),
                })}
              </p>
            )}
          </div>
        </div>
        <Badge className={cn("shrink-0", getStatusStyles(formStatus))}>
          {formatKeyText(t(formStatus))}
        </Badge>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex justify-between gap-2 items-center">
          <span className="text-muted-foreground">
            {t("tax_submission_id")}
          </span>
          <CopyableId
            id={item.submission_id}
            ariaLabel="tax_submission_id"
            className="font-semibold"
          />
        </div>
        <div className="flex justify-between gap-2">
          <span className="text-muted-foreground">
            {t("tax_delivery_method")}
          </span>
          <span className="font-semibold capitalize">
            {t("tax_" + item.delivery_method)}
          </span>
        </div>
      </div>

      {action && <div className="mt-4 flex justify-end">{action}</div>}
    </div>
  );
};
